import { Deviation_Survey_Directional_Dataset_Enum } from "gql/api";
import type { ConvertableUnit } from "@/libs/units";
import type { DeviationSurveyEntryRows } from "@/composables/scenarioCalculations/calculationTypes";
import {
  getTrueVerticalDepthFor3DFt,
  inclinationBetweenPointsDeg,
  getTrueVerticalDepthFor2DFt,
  getDogLegSeverityDeg100Ft,
  getRowPhiRad
} from "./geometry";

export function calculateDeviationSurveyRowData(
  entry_rows: DeviationSurveyEntryRows,
  depthUnit: ConvertableUnit
): number[][] {
  if ((entry_rows.data?.length || 0) === 0) return [];

  const rows: number[][] = [];

  const is3dMdInclAz =
    entry_rows.surveyType ===
    Deviation_Survey_Directional_Dataset_Enum.ThreeDMdInclinationAzimuth;

  const is2dMdTVD =
    entry_rows.surveyType ===
    Deviation_Survey_Directional_Dataset_Enum.TwoDMdTvd;

  let prevTvd = 0;
  let prevIncl = 0;
  let prevAz = 0;

  for (let i = 0; i < entry_rows.data.length; i++) {
    const prevRow = entry_rows.data[i - 1] || [0, 0, 0];
    const currentRow = entry_rows.data[i];

    // Measured depth is always the first value, no matter which type of survey it is
    // Convert measured depth to standard unit
    const md = depthUnit.toStandardUnit(currentRow[0]);
    const prevMD = depthUnit.toStandardUnit(prevRow[0]);
    // Azimuth only given in 3dMdInclAz survey types so we can only calculate DLS
    // for this type?. Else DLS is just 0
    const azimuth = is3dMdInclAz ? currentRow[2] : 0;

    let tvd;
    let inclination;

    if (is3dMdInclAz) {
      inclination = currentRow[1];
      tvd = getTrueVerticalDepthFor3DFt(
        md,
        prevMD,
        inclination,
        prevIncl,
        prevTvd,
        azimuth,
        prevAz
      );
      prevIncl = inclination; // Update previous inclination for next iteration
      prevAz = azimuth; // Update previous azimuth for next iteration
    } else if (is2dMdTVD) {
      // currentRow[1] is tvd when is2dMdTVD else is Inclination Angle
      // Convert to standard length unit in 2dMdTVD mode
      currentRow[1] = depthUnit.toStandardUnit(currentRow[1]);
      tvd = currentRow[1];
      inclination = inclinationBetweenPointsDeg(
        prevMD,
        md,
        prevRow?.[1] || 0,
        tvd
      );
    } else {
      // is the TwoDMdInclination
      inclination = currentRow[1];
      tvd = getTrueVerticalDepthFor2DFt(md, prevMD, inclination, prevTvd);
    }

    const dls = is3dMdInclAz
      ? getDogLegSeverityDeg100Ft(
          prevMD,
          md,
          prevRow?.[1] || 0,
          inclination,
          prevRow?.[2] || 0,
          azimuth
        )
      : 0;

    prevTvd = tvd;

    rows.push([md, inclination, azimuth, tvd, dls]);
  }

  return rows;
}

// These calcs are from /static/Friction Calcs foo.xlsx
export function getHydrostaticSumForPiping(
  dataRows: DeviationSurveyEntryRows
): number {
  let hydrostaticSum = 0;
  let phiRad = 0;

  dataRows.data.forEach((row, i) => {
    phiRad = getRowPhiRad(row[1]);
    // Skip top tubing first row
    if (i === 0) {
      return;
    }
    hydrostaticSum += (row[0] - dataRows.data[i - 1]?.[0]) * Math.sin(phiRad);
  });
  return hydrostaticSum;
}
